/**
 * BlockPicker — Master Graph Playground 의 오른쪽 패널.
 *
 * 카테고리별 아코디언 + 블럭 토글 버튼 목록. 검색 input 으로 빠르게 찾고, 카테고리별
 * "전체 ON / 전체 OFF" 로 그룹 조작. 개별 블럭 클릭은 즉시 상위 `selectedIds` 에 반영되어
 * 왼쪽 그래프가 실시간으로 갱신된다.
 *
 * 아코디언 펼침 상태는 `localStorage` 에 저장해 세션 간 유지.
 */
import { useEffect, useMemo, useState } from "react"
import type { BlockCategory, OntologyBlock } from "../../api/admin-client.ts"
import { matchesQuery } from "../../hooks/useTextFilter.ts"

/** 카테고리 순서 (BlocksPage 와 일치). */
const CATEGORY_ORDER: ReadonlyArray<{ readonly key: BlockCategory; readonly label: string }> = [
	{ key: "actor", label: "Actor" },
	{ key: "content", label: "Content" },
	{ key: "event", label: "Event" },
	{ key: "product", label: "Product" },
	{ key: "place", label: "Place" },
	{ key: "structure", label: "Structure" },
	{ key: "decision", label: "Decision" }
]

const STORAGE_KEY = "pk.blockPicker.openCategories"

function loadOpenSet(): Set<BlockCategory> {
	if (typeof window === "undefined") return new Set(CATEGORY_ORDER.map((c) => c.key))
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY)
		if (!raw) return new Set(CATEGORY_ORDER.map((c) => c.key))
		const arr = JSON.parse(raw) as ReadonlyArray<BlockCategory>
		return new Set(arr)
	} catch {
		return new Set(CATEGORY_ORDER.map((c) => c.key))
	}
}

function saveOpenSet(s: ReadonlySet<BlockCategory>): void {
	if (typeof window === "undefined") return
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...s]))
	} catch {
		/* ignore */
	}
}

export interface BlockPickerProps {
	readonly blocks: ReadonlyArray<OntologyBlock>
	readonly selectedIds: ReadonlySet<string>
	readonly onToggle: (blockId: string) => void
	readonly onToggleCategory: (category: BlockCategory, activate: boolean) => void
	readonly onClearAll: () => void
}

export function BlockPicker(props: BlockPickerProps) {
	const { blocks, selectedIds, onToggle, onToggleCategory, onClearAll } = props
	const [query, setQuery] = useState("")
	const [openSet, setOpenSet] = useState<ReadonlySet<BlockCategory>>(() => loadOpenSet())

	useEffect(() => {
		saveOpenSet(openSet)
	}, [openSet])

	const toggleOpen = (cat: BlockCategory) => {
		setOpenSet((prev) => {
			const next = new Set(prev)
			if (next.has(cat)) next.delete(cat)
			else next.add(cat)
			return next
		})
	}

	// 카테고리별 블럭 그룹핑 + 검색 필터
	const grouped = useMemo(() => {
		const result: Array<{
			readonly cat: BlockCategory
			readonly label: string
			readonly items: ReadonlyArray<OntologyBlock>
			readonly selectedCount: number
			readonly totalCount: number
		}> = []
		for (const { key, label } of CATEGORY_ORDER) {
			const all = blocks.filter((b) => b.category === key)
			const items = all.filter((b) => matchesQuery(query, [b.displayName, b.id, b.description]))
			const selectedCount = all.filter((b) => selectedIds.has(b.id)).length
			result.push({ cat: key, label, items, selectedCount, totalCount: all.length })
		}
		return result
	}, [blocks, selectedIds, query])

	const totalSelected = selectedIds.size
	const totalBlocks = blocks.length

	return (
		<aside className="block-picker" aria-label="블럭 조합 선택 패널">
			<div className="block-picker__head">
				<div className="block-picker__title">
					블럭 선택
					<span className="block-picker__count" aria-live="polite">
						{totalSelected} / {totalBlocks}
					</span>
				</div>
				<label className="table-search block-picker__search">
					<svg
						className="icon"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<title>블럭 검색</title>
						<circle cx="11" cy="11" r="7" />
						<path d="m21 21-4.3-4.3" />
					</svg>
					<input
						type="search"
						placeholder="블럭 이름/ID 검색"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						spellCheck={false}
					/>
				</label>
			</div>

			<div className="block-picker__body">
				{grouped.map((g) => {
					const open = openSet.has(g.cat) || query.trim().length > 0
					const allOn = g.selectedCount === g.totalCount && g.totalCount > 0
					return (
						<section
							key={g.cat}
							className={`block-picker__section${open ? " is-open" : ""}`}
							aria-label={`${g.label} 카테고리`}
						>
							<div className="block-picker__section-head">
								<button
									type="button"
									className="block-picker__section-toggle"
									onClick={() => toggleOpen(g.cat)}
									aria-expanded={open}
								>
									<span className={`blocks-page-graph-legend-swatch cat-${g.cat}`} aria-hidden />
									<span className="block-picker__section-label">{g.label}</span>
									<span className="block-picker__section-count">
										{g.selectedCount}/{g.totalCount}
									</span>
									<svg
										className={`block-picker__chevron${open ? " is-open" : ""}`}
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										aria-hidden="true"
									>
										<title>토글</title>
										<polyline points="6 9 12 15 18 9" />
									</svg>
								</button>
								{g.totalCount > 0 && (
									<button
										type="button"
										className="block-picker__group-btn"
										onClick={() => onToggleCategory(g.cat, !allOn)}
										title={allOn ? "이 카테고리 전부 끄기" : "이 카테고리 전부 켜기"}
									>
										{allOn ? "전부 끄기" : "전부 켜기"}
									</button>
								)}
							</div>
							{open && (
								<ul className="block-picker__list">
									{g.items.length === 0 ? (
										<li className="block-picker__empty">매칭되는 블럭이 없습니다.</li>
									) : (
										g.items.map((b) => {
											const on = selectedIds.has(b.id)
											return (
												<li key={b.id}>
													<button
														type="button"
														className={`block-picker__item${on ? " is-on" : ""}`}
														aria-pressed={on}
														onClick={() => onToggle(b.id)}
														title={b.description}
													>
														<span className="block-picker__item-tick" aria-hidden>
															{on ? "✓" : "+"}
														</span>
														<span className="block-picker__item-name">{b.displayName}</span>
														<span className="block-picker__item-kind">{b.slice.kinds[0]?.name ?? b.id}</span>
													</button>
												</li>
											)
										})
									)}
								</ul>
							)}
						</section>
					)
				})}
			</div>

			<div className="block-picker__foot">
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					onClick={onClearAll}
					disabled={totalSelected === 0}
				>
					전체 해제
				</button>
			</div>
		</aside>
	)
}
